import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { APPLICATION_ROLES } from 'constants';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { AnketaProgressContext } from 'components/modules/Widgets/AnketaProgress';
import { deleteDocumetsType, setIsChangeValues, setIsPEPSignature } from 'store/anketa';
import { realtyStateOwn } from 'store/anketa/mappers/livingAddressProperty';
import { RELATION_TYPE_FRIEND } from 'store/anketa/mappers/trustees';
import { setOtpId, setTimerStart } from 'store/confirmSMS';
import { isViewClientWithApprovalCheckbox } from 'store/user/selectors';

import { KEY_COMPONENTS } from '../../MainAnketa.constants';
import { getPassportConfidenceErrors } from '../../MainAnketa.helpers';

import { DOCUMENTS_TYPES } from '../FileUpload/FileUpload.constants';

import { EMPLOYMENT_TYPES, PERSONAL_INFO_SUB_TITLES } from './PersonalInfo.constants';
import {
  checkChangeValues,
  docGridDefault,
  getNewTrustee,
  getUniqueClassesTemplate,
  getUniqueDocumentsClassifications,
} from './PersonalInfo.helpers';

export const usePersonalInfoPresenter = ({
  data,
  params,
  validate,
  forcedValidation,
  onChange,
}) => {
  const rootDispatch = useDispatch();
  const refs = {
    passport: useRef(null),
    seconddocument: useRef(null),
  };

  const { showErrors, collapsed, updateCollapsed } = useContext(AnketaProgressContext);

  const { service } = params;
  const { persons, insurance, currentPersonId } = data;
  const { trustees, livingAddressProperty, personalInfo } = persons[currentPersonId];

  const userRole = useSelector((state) => {
    return state.user.role;
  });
  const isUserAdmin = userRole === APPLICATION_ROLES.ADMINISTRATOR;
  const { documents, isPEPSignature } = useSelector((state) => {
    return state.anketa;
  });
  const { doctypes } = useSelector((state) => {
    return state.referenceBooks;
  });
  const isClientWithApprovalCheckbox = useSelector(isViewClientWithApprovalCheckbox);

  const [uniqueClasses, setUniqueClasses] = useState(null);
  const [docGridTemplate, setDocGridTemplate] = useState(docGridDefault);
  const [passportConfidenceErrors, setPassportConfidenceErrors] = useState({});
  const [showAttention, setShowAttention] = useState(false);
  const [showAttentionFirstTime, setShowAttentionFirstTime] = useState(true);

  const isViewSelfAgreeCheckbox = !service && (isClientWithApprovalCheckbox || isUserAdmin);

  const changeHandlerPersons = useCallback(
    ({ values, key, id = currentPersonId }) => {
      const workData = { ...(data.persons[id]?.work || {}) };
      const isChangeValues = checkChangeValues(values, key, persons[currentPersonId]);

      if (isChangeValues) {
        if (isPEPSignature) {
          rootDispatch(deleteDocumetsType(DOCUMENTS_TYPES.PERSONAL_DATA));
          rootDispatch(setIsPEPSignature(false));
          rootDispatch(setIsChangeValues(true));
        }

        rootDispatch(setOtpId(null));
        rootDispatch(setTimerStart(0));
      }

      if (key === KEY_COMPONENTS.ADDRESS_REG) {
        if (workData?.employmentType?.sysName === EMPLOYMENT_TYPES.INDIVIDUAL_ENTREPRENEUR_3) {
          workData.organization.legalAddress = { ...values };

          if (workData.organization.actualAddressEquals) {
            workData.organization.actualAddress = { ...values };
          }
        }
      }

      if (key === KEY_COMPONENTS.WORK) {
        workData.educationType = values.educationType;
      }

      onChange({
        insurance,
        persons: { ...persons, [id]: { ...persons[id], [key]: values, work: workData } },
      });
    },
    [data.persons, persons, insurance, currentPersonId, isPEPSignature, onChange, rootDispatch]
  );

  const changeHandlerInsur = useCallback(
    (obj, key) => {
      onChange({ persons, insurance: { ...insurance, [key]: { ...insurance[key], ...obj } } });
    },
    [persons, insurance, onChange]
  );

  const changeTrustHandler = useCallback(
    ({ values, id }) => {
      onChange({
        insurance,
        persons: {
          ...persons,
          [currentPersonId]: {
            ...persons[currentPersonId],
            trustees: { ...trustees, [id]: values },
          },
        },
      });
    },
    [persons, insurance, currentPersonId, trustees, onChange]
  );

  const addTrustee = useCallback(() => {
    const addId = uuidv4();
    const newTrustees = { ...trustees, [addId]: getNewTrustee(addId) };

    changeHandlerPersons({ values: newTrustees, key: KEY_COMPONENTS.TRUSTEES });
  }, [trustees, changeHandlerPersons]);

  const getSubHeader = useMemo(() => {
    return !uniqueClasses || uniqueClasses.length === 0
      ? PERSONAL_INFO_SUB_TITLES.UPLOAD_DOCUMENTS
      : PERSONAL_INFO_SUB_TITLES.PERSONS_DOCUMENTS;
  }, [uniqueClasses]);

  const getSelfAgreeValue = useCallback(() => {
    return persons[currentPersonId]?.selfAgreement;
  }, [persons, currentPersonId]);

  const handleSelfAgreeChange = useCallback(() => {
    changeHandlerPersons({
      key: 'selfAgreement',
      values: !getSelfAgreeValue(),
    });

    updateCollapsed(
      Object.fromEntries(
        Object.keys(collapsed).map((key) => {
          return [key, false];
        })
      )
    );
  }, [updateCollapsed, collapsed, changeHandlerPersons, getSelfAgreeValue]);

  const getPersonalFormValidate = useCallback(() => {
    return (
      validate ||
      showErrors.some((key) => {
        return key === KEY_COMPONENTS.PERSONAL_INFO_FORM;
      }) ||
      forcedValidation.includes(KEY_COMPONENTS.PERSONAL_INFO_FORM)
    );
  }, [validate, forcedValidation, showErrors]);

  const getPassportFormValidate = useCallback(() => {
    const hasShowErrors = showErrors.some((key) => {
      return key === KEY_COMPONENTS.PERSONAL_INFO_FORM || key === KEY_COMPONENTS.PASSPORT;
    });

    const hasForcedValidation =
      forcedValidation.includes(KEY_COMPONENTS.PERSONAL_INFO_FORM) ||
      forcedValidation.includes(KEY_COMPONENTS.PASSPORT);

    return validate || hasShowErrors || hasForcedValidation;
  }, [validate, forcedValidation, showErrors]);

  const getSecondDocFormValidate = useCallback(() => {
    return (
      validate ||
      showErrors.some((key) => {
        return key === KEY_COMPONENTS.SECOND_DOCUMENT;
      }) ||
      forcedValidation.includes(KEY_COMPONENTS.SECOND_DOCUMENT)
    );
  }, [validate, forcedValidation, showErrors]);

  const getAddressFormValidate = useCallback(() => {
    return (
      validate ||
      showErrors.some((key) => {
        return (
          key === KEY_COMPONENTS.ADDRESS_REG ||
          key === KEY_COMPONENTS.ADDRESS_LIVE ||
          key === KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY
        );
      }) ||
      forcedValidation.includes(KEY_COMPONENTS.ADDRESS_REG) ||
      forcedValidation.includes(KEY_COMPONENTS.ADDRESS_LIVE) ||
      forcedValidation.includes(KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY)
    );
  }, [validate, forcedValidation, showErrors]);

  const getTrusteesFormValidate = useCallback(() => {
    return (
      validate ||
      showErrors.some((key) => {
        return key === KEY_COMPONENTS.TRUSTEES || key === KEY_COMPONENTS.SPOUSE;
      }) ||
      forcedValidation.includes(KEY_COMPONENTS.TRUSTEES) ||
      forcedValidation.includes(KEY_COMPONENTS.SPOUSE)
    );
  }, [validate, forcedValidation, showErrors]);

  const getUniqueClassesDocuments = useCallback(() => {
    return documents && uniqueClasses;
  }, [documents, uniqueClasses]);

  const clearPassportConfidenceErrors = useCallback((path) => {
    setPassportConfidenceErrors((prev) => {
      const newErrors = { ...prev };

      delete newErrors[path];

      return newErrors;
    });
  }, []);

  const showRecognizeAttentionCard = useCallback(() => {
    return showAttention && showAttentionFirstTime;
  }, [showAttention, showAttentionFirstTime]);

  useEffect(() => {
    const trusteesKeys = Object.keys(trustees);
    const trusteesCount = trusteesKeys.length;
    const [firstTrusteeKey] = trusteesKeys;

    if (trusteesCount === 0) {
      addTrustee();
    } else {
      let needToUpdate = false;
      const updatedTrustees = { ...trustees };

      if (trusteesCount > 1) {
        Object.keys(updatedTrustees).forEach((key) => {
          if (key !== firstTrusteeKey) {
            delete updatedTrustees[key];
          }
        });

        needToUpdate = true;
      }

      if (!updatedTrustees[firstTrusteeKey]?.relationType?.id) {
        updatedTrustees[firstTrusteeKey] = {
          ...updatedTrustees[firstTrusteeKey],
          relationType: RELATION_TYPE_FRIEND,
        };

        needToUpdate = true;
      }

      if (needToUpdate) {
        changeHandlerPersons({ values: updatedTrustees, key: KEY_COMPONENTS.TRUSTEES });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustees, changeHandlerPersons]);

  useEffect(() => {
    const validateTimeout = setTimeout(() => {
      if (documents) {
        const errors = getPassportConfidenceErrors(documents);

        setPassportConfidenceErrors(errors);
      }
    }, 1500);

    return () => {
      return clearTimeout(validateTimeout);
    };
  }, [documents]);

  useEffect(() => {
    if (!livingAddressProperty?.realtyState?.id && realtyStateOwn) {
      const updatedProperty = {
        ...livingAddressProperty,
        realtyState: { ...realtyStateOwn },
      };

      changeHandlerPersons({
        values: updatedProperty,
        key: KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livingAddressProperty, realtyStateOwn, changeHandlerPersons]);

  useEffect(() => {
    if (!uniqueClasses || uniqueClasses.length === 0) {
      setDocGridTemplate(docGridDefault);
    } else {
      const template = getUniqueClassesTemplate(uniqueClasses);

      setDocGridTemplate(template);
    }
  }, [uniqueClasses]);

  useEffect(() => {
    if (documents && doctypes) {
      setUniqueClasses(getUniqueDocumentsClassifications(documents));
    }
  }, [documents, doctypes]);

  useEffect(() => {
    setShowAttention(Object.keys(passportConfidenceErrors).length !== 0);
  }, [passportConfidenceErrors]);

  useEffect(() => {
    setShowAttentionFirstTime(true);
  }, []);

  useEffect(() => {
    if (documents.length === 0) {
      setShowAttentionFirstTime(true);
    }
  }, [documents]);

  return {
    refs,
    persons,
    service,
    doctypes,
    insurance,
    getSubHeader,
    personalInfo,
    uniqueClasses,
    docGridTemplate,
    currentPersonId,
    passportConfidenceErrors,
    isViewSelfAgreeCheckbox,
    setShowAttention,
    getSelfAgreeValue,
    changeTrustHandler,
    changeHandlerInsur,
    changeHandlerPersons,
    handleSelfAgreeChange,
    getAddressFormValidate,
    getPassportFormValidate,
    getPersonalFormValidate,
    getTrusteesFormValidate,
    getSecondDocFormValidate,
    getUniqueClassesDocuments,
    setShowAttentionFirstTime,
    showRecognizeAttentionCard,
    clearPassportConfidenceErrors,
  };
};
